import axios from '../helpers/axios';
import { handleErrorWithRedirectIfNotAuth } from '../errors';
import { notify } from 'reapop';
import { accessRulesAction, changeModalState } from '../modules/actions';
import type { AppThunk } from '../typings/redux';
import { getTranslationKey } from '../modules/utils';

export const createCollection =
  ({
    title,
    name,
    eventDate,
    userBrand,
    templateId,
    galleryPresetId,
    faceRecognition
  }: any): AppThunk<Promise<{ collection_id?: string; err?: string }>> =>
  async (dispatch) => {
    try {
      const res = await axios.post('/api/collection/create/', {
        title,
        name,
        eventDate,
        userBrand,
        templateId,
        galleryPresetId,
        faceRecognition
      });
      dispatch(accessRulesAction());

      return { collection_id: res.data._id };
    } catch (e: any) {
      if (e?.response?.status === 402) {
        dispatch(changeModalState({ key: 'collectionModal', state: false }));
        dispatch(
          changeModalState({ key: 'upgradeModal', state: true, desiredAction: 'collections' })
        );
        dispatch({ type: 'end_current_help_campaign_step' });

        return { err: 'paymentRequired' };
      }
      if (e?.response?.status === 426) return { err: 'templatePurchase' };
      handleErrorWithRedirectIfNotAuth(e);

      return { err: 'generalError' };
    }
  };

export const getScrappbookTemplates = (): AppThunk<Promise<void>> => async (dispatch) => {
  try {
    const res = await axios.get('/api/scrappbookGalleryTemplates');
    dispatch({ type: 'SET_SCRAPPBOOK_TEMPLATES', payload: res.data });
  } catch (e) {
    console.log(e);
  }
};

interface CollectionsIdsResponse {
  error?: string;
  result?: string[];
}

export const getCollectionsIds =
  ({
    withInitialCollectionsCount
  }: {
    withInitialCollectionsCount: boolean;
  }): AppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      const collectionsIds = await axios.get<CollectionsIdsResponse>('/api/collections/ids');
      if (collectionsIds.data.error) throw new Error(collectionsIds.data.error);
      dispatch({ type: 'SET_COLLECTION_IDS', payload: collectionsIds.data.result });
      if (withInitialCollectionsCount) {
        dispatch({
          type: 'SET_INITIAL_COLLECTIONS_AMOUNT',
          payload: collectionsIds.data.result?.length || 0
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

export const getCollectionPasswordById =
  (collection_id: string): AppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      const res = await axios.get('/api/collection/password', { params: { collection_id } });
      if (res.data.password !== null) {
        dispatch({
          type: 'UPDATE_COLLECTION_BY_PATH',
          payload: { path: 'password', collection_id, value: res.data.password }
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

export const getCollectionDownloadPinById =
  (collection_id: string): AppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      const res = await axios.get('/api/collection/downloadPin', { params: { collection_id } });
      dispatch({
        type: 'UPDATE_COLLECTION_BY_PATH',
        payload: { path: 'downloadPin', collection_id, value: res.data.downloadPin }
      });
    } catch (e) {
      console.log(e);
    }
  };

export const getCollectionById =
  (collection_id: string): AppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      const res = await axios.get('/api/collection', {
        params: { id: collection_id, ownerRequest: true }
      });
      const { data: images } = await axios.get('/api/images', { params: { collection_id } });
      const site = res.data.result.data;
      site.images = images;
      dispatch({ type: 'ADD_COLLECTION', payload: site });
      dispatch({ type: 'FETCH_COLLECTION_GALLERYS', payload: site });
    } catch (e) {
      console.log(e);
    }
  };

export const updateCollectionValueByPath =
  (
    collection_id: string,
    path: string,
    value: any,
    sendToServer = false
  ): AppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      let res: any = null;
      if (sendToServer) {
        res = await axios.put('/api/collection/update', { collection_id, path, value });
        dispatch(
          notify({
            title: getTranslationKey('success'),
            message: getTranslationKey('saved'),
            status: 'success',
            position: 'top-center',
            dismissible: true,
            dismissAfter: 1500
          })
        );
        dispatch({
          type: 'UPDATE_COLLECTION_BY_PATH',
          payload: { path: res.data.path, value: res.data.value, collection_id }
        });
      } else {
        dispatch({ type: 'UPDATE_COLLECTION_BY_PATH', payload: { path, value, collection_id } });
      }
    } catch (e) {
      console.log(e);
      handleErrorWithRedirectIfNotAuth(e);
    }
  };

export const deleteGalleryFromCollection =
  (data: any): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      await axios.delete('/api/collection/gallery', { params: data });
      dispatch({ type: 'DELETE_GALLERY_FROM_COLLECTION', payload: data });
      dispatch({ type: 'REMOVE_IMAGES_FROM_SELECTIONS', payload: data });
      dispatch(
        notify({
          title: getTranslationKey('success'),
          message: getTranslationKey('saved'),
          status: 'success',
          position: 'top-center',
          dismissible: true,
          dismissAfter: 1500
        })
      );

      return true;
    } catch (e) {
      console.log(e);

      return false;
    }
  };

export const orderGallerys =
  (data: any): AppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      axios.post('/api/collection/gallery/order', {
        gallerys: data.sortedGallerys.map((gal: any) => gal._id),
        collection_id: data.collection_id
      });
      dispatch({ type: 'ORDER_COLLECTION_GALLERYS', payload: data });
      dispatch(
        notify({
          title: getTranslationKey('success'),
          message: getTranslationKey('saved'),
          status: 'success',
          position: 'top-center',
          dismissible: true,
          dismissAfter: 1500
        })
      );
    } catch (e) {
      console.log(e);
    }
  };
